import { resolveContentMode } from "@/content/mode";
import { getVisibleProjects } from "@/content/projects";
import { CursorController } from "@/controllers/CursorController";
import { HeaderProgressController } from "@/controllers/HeaderProgressController";
import { LoaderController } from "@/controllers/LoaderController";
import { SectionNavigationController } from "@/controllers/SectionNavigationController";
import { SocialDockController } from "@/controllers/SocialDockController";
import { applyMetadata, getProjectMetadata, homeMetadata, notFoundMetadata } from "@/meta/metadata";
import { Router } from "@/router/Router";
import type { NavigationIntent } from "@/router/Router";
import { matchRoute } from "@/router/routes";
import type { RouteMatch } from "@/router/routes";
import { createScrollController } from "./scroll";
import type { ViewContext, ViewController, ViewTransitionContext } from "./types";

function requiredElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Required element is missing: ${selector}`);
  return element;
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

function settleWithin(promise: Promise<void>, milliseconds: number): Promise<void> {
  return Promise.race([promise, new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds))]);
}

const VIEW_READY_TIMEOUT_MS = 1_600;
const VIEW_LEAVE_TIMEOUT_MS = 1_200;
const VIEW_ENTER_TIMEOUT_MS = 2_800;

export class App {
  readonly #siteShell = requiredElement<HTMLElement>("#site-shell");
  readonly #outlet = requiredElement<HTMLElement>("#app");
  readonly #scroll = createScrollController();
  readonly #contentMode = resolveContentMode(import.meta.env.VITE_CONTENT_MODE, import.meta.env.DEV);
  readonly #loader = new LoaderController(requiredElement<HTMLElement>("[data-loader]"));
  readonly #cursor = new CursorController(
    requiredElement<HTMLElement>("[data-cursor]"),
    requiredElement<HTMLElement>("[data-cursor-dot]"),
    requiredElement<HTMLElement>("[data-cursor-outline]")
  );
  readonly #headerProgress = new HeaderProgressController(requiredElement<HTMLElement>(".site-header__logo"), this.#scroll);
  readonly #socialDock = new SocialDockController(requiredElement<HTMLElement>("[data-social-dock-host]"));
  readonly #router = new Router((intent) => this.#render(intent));
  readonly #viewContext: ViewContext = { contentMode: this.#contentMode, scroll: this.#scroll };
  readonly #sectionNavigation = new SectionNavigationController({
    root: this.#siteShell,
    outlet: this.#outlet,
    scroll: this.#scroll,
    isHome: () => this.#currentRoute?.name === "home",
    ensureHome: () => this.#ensureHomeForSectionNavigation(),
  });
  #currentView?: ViewController;
  #currentRoute?: RouteMatch;
  #currentPath = "";
  #navigationSequence = 0;
  #initialRender = true;

  async start(): Promise<void> {
    this.#socialDock.mount();
    this.#cursor.mount();
    this.#headerProgress.mount();
    this.#sectionNavigation.mount();
    this.#scroll.stop();
    this.#syncProjectNavigation();

    try {
      await this.#router.start();
    } catch (error) {
      this.#renderFatalError(error);
      this.#socialDock.bindFooter(null);
      await this.#loader.finish(Promise.resolve());
      this.#socialDock.enter();
    }
  }

  destroy(): void {
    this.#currentView?.destroy();
    this.#sectionNavigation.destroy();
    this.#headerProgress.destroy();
    this.#socialDock.destroy();
    this.#router.destroy();
    this.#cursor.destroy();
    this.#scroll.destroy();
  }

  async #render(intent: NavigationIntent): Promise<void> {
    const route = matchRoute(intent.url.pathname);
    const normalizedPath = intent.url.pathname.replace(/\/$/, "") || "/";

    if (normalizedPath === this.#currentPath && this.#currentView) {
      this.#currentView.update?.(intent.url);
      await this.#applyScroll(intent);
      this.#headerProgress.refresh();
      this.#socialDock.refresh();
      this.#outlet.dataset.routeReady = normalizedPath;
      return;
    }

    const sequence = ++this.#navigationSequence;
    const transition = this.#createTransitionContext(route, normalizedPath, intent.source);
    this.#outlet.setAttribute("aria-busy", "true");
    delete this.#outlet.dataset.routeReady;
    document.body.classList.add("is-transitioning");
    this.#scroll.stop();

    await settleWithin(this.#leaveCurrentView(transition), VIEW_LEAVE_TIMEOUT_MS);

    if (sequence !== this.#navigationSequence) return;
    this.#currentView?.destroy();
    this.#currentView = undefined;
    this.#outlet.removeAttribute("data-missing-project");

    let view: ViewController;
    if (route.name === "home") {
      const { HomeView } = await import("@/views/HomeView");
      view = new HomeView();
      applyMetadata(homeMetadata);
    } else if (route.name === "project") {
      const { ProjectView } = await import("@/views/ProjectView");
      const projectView = new ProjectView(route.params.slug, this.#contentMode);
      view = projectView;
      applyMetadata(projectView.project ? getProjectMetadata(projectView.project) : notFoundMetadata);
    } else {
      const { NotFoundView } = await import("@/views/NotFoundView");
      view = new NotFoundView();
      applyMetadata(notFoundMetadata);
    }

    if (sequence !== this.#navigationSequence) {
      view.destroy();
      return;
    }

    view.mount(this.#outlet, this.#viewContext);
    this.#currentView = view;
    this.#currentRoute = route;
    this.#currentPath = normalizedPath;
    document.body.dataset.route = route.name;
    this.#scroll.resize();
    this.#scroll.start();
    await this.#applyScroll(intent);
    this.#headerProgress.refresh();
    this.#socialDock.bindFooter(this.#outlet.querySelector<HTMLElement>(".social-footer"));

    if (this.#initialRender) {
      this.#initialRender = false;
      // `finish` returns as the curtain starts lifting, so the copy fade and the particle assembly
      // begin during the fade rather than after it.
      await this.#loader.finish(view.ready);
      this.#socialDock.enter();
      await Promise.all([this.#enterView(view, transition), this.#loader.faded]);
    } else {
      const visualReadiness = transition.fromProjectSlug && route.name === "home" ? Promise.resolve() : settleWithin(view.ready, VIEW_READY_TIMEOUT_MS);
      await Promise.all([visualReadiness, settleWithin(this.#enterView(view, transition), VIEW_ENTER_TIMEOUT_MS)]);
    }

    if (sequence !== this.#navigationSequence) return;

    if (intent.source === "push") {
      this.#outlet.focus({ preventScroll: true });
    }

    this.#outlet.dataset.routeReady = normalizedPath;
    this.#outlet.removeAttribute("aria-busy");
    document.body.classList.remove("is-transitioning");
  }

  async #leaveCurrentView(transition: ViewTransitionContext): Promise<void> {
    if (transition.coveredBySectionTransition) return;
    try {
      await this.#currentView?.leave?.(transition);
    } catch (error) {
      console.error("The current view could not complete its exit animation.", error);
    }
  }

  async #enterView(view: ViewController, transition: ViewTransitionContext): Promise<void> {
    try {
      await view.enter?.(transition);
    } catch (error) {
      console.error("The new view could not complete its entrance animation.", error);
    }
  }

  #createTransitionContext(route: RouteMatch, normalizedPath: string, source: NavigationIntent["source"]): ViewTransitionContext {
    return {
      source,
      fromPath: this.#currentPath,
      toPath: normalizedPath,
      coveredBySectionTransition: this.#siteShell.classList.contains("is-section-navigating"),
      fromProjectSlug: this.#currentRoute?.name === "project" ? this.#currentRoute.params.slug : undefined,
      toProjectSlug: route.name === "project" ? route.params.slug : undefined,
    };
  }

  #syncProjectNavigation(): void {
    const item = document.querySelector<HTMLElement>("[data-projects-navigation]");
    if (!item) return;
    const hasProjects = getVisibleProjects(this.#contentMode).length > 0;
    item.hidden = !hasProjects;
    item.setAttribute("aria-hidden", String(!hasProjects));
  }

  async #ensureHomeForSectionNavigation(): Promise<void> {
    if (this.#currentRoute?.name === "home" && this.#currentView) return;
    await this.#router.navigate("/");
  }

  async #applyScroll(intent: NavigationIntent): Promise<void> {
    await nextFrame();
    this.#scroll.resize();

    if (intent.url.hash) {
      const target = document.getElementById(decodeURIComponent(intent.url.hash.slice(1)));
      if (target) {
        this.#scroll.scrollTo(target, { immediate: intent.source !== "push" });
        return;
      }
    }

    const targetY = intent.source === "pop" ? (intent.restoreScrollY ?? 0) : 0;
    this.#scroll.scrollTo(targetY, { immediate: true, force: true });
  }

  #renderFatalError(error: unknown): void {
    console.error(error);
    applyMetadata(notFoundMetadata);
    this.#outlet.innerHTML = `
      <section class="not-found" data-view="fatal-error">
        <p class="not-found__code">Error</p>
        <h1>The portfolio could not start.</h1>
        <p>Please refresh the page or try again later.</p>
      </section>`;
    this.#outlet.removeAttribute("aria-busy");
    document.body.classList.remove("is-transitioning");
    this.#scroll.start();
  }
}
